import { useEffect, useId, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { btnGhost, btnIcon, inputCls } from '../ui/styles';
import { SettingNumber } from './SettingNumber';
import { useSettings } from './useSettings';
import type { PauseOption } from '../../core/types/site';

/** "4, 6, 8" → [4, 6, 8]; null bij iets ongeldigs (leeg, geen getal, buiten 1–52). */
const parseWeeks = (raw: string): number[] | null => {
  const parts = raw.split(/[,\s]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const weeks = parts.map(Number);
  if (weeks.some(w => !Number.isInteger(w) || w < 1 || w > 52)) return null;
  return Array.from(new Set(weeks)).sort((a, b) => a - b);
};

/** Instellingen › Webshop & verzending › Bewust Abonnement. */
export function SubscriptionSettings() {
  const {
    policy,
    patchPolicy
  } = useSettings();
  const sub = policy.subscription;
  const set = (patch: Partial<typeof sub>) => patchPolicy('subscription', patch);

  // Ritmes als gewone tekst; pas bij verlaten van het veld bewaard, zodat typen niet hapert.
  const intervalsId = useId();
  const [intervalsText, setIntervalsText] = useState(sub.intervals.join(', '));
  const [intervalsError, setIntervalsError] = useState<string | null>(null);
  useEffect(() => setIntervalsText(sub.intervals.join(', ')), [sub.intervals]);
  const commitIntervals = () => {
    const weeks = parseWeeks(intervalsText);
    if (!weeks) {
      setIntervalsError('Vul hele weken in, gescheiden door een komma, bijv. 4, 6, 8.');
      return;
    }
    setIntervalsError(null);
    // Het standaardritme moet een van de keuzes blijven.
    const fallback = weeks.includes(sub.defaultIntervalWeeks) ? sub.defaultIntervalWeeks : weeks[0];
    set({
      intervals: weeks,
      defaultIntervalWeeks: fallback
    });
  };
  const setPause = (i: number, patch: Partial<PauseOption>) => set({
    pauseOptions: sub.pauseOptions.map((p, j) => j === i ? {
      ...p,
      ...patch
    } : p)
  });
  return <Panel title="Bewust Abonnement" description="De regels van het productabonnement. Klanten zien ze in de winkel en in Mijn omgeving." data-id="element-4245" data-mp="2y5ila7wh1l45">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-4246" data-mp="3859dg2xk7o12">
        <SettingNumber label="Cadeau bij elke … levering" hint="Bijvoorbeeld 3: de derde, zesde, negende levering." value={sub.giftEvery} onChange={v => set({
        giftEvery: v
      })} min={1} max={24} data-id="element-4247" data-mp="383g7yqyon9un" />
        <SettingNumber label="Huidchecks per jaar (video)" value={sub.videoChecksPerYear} onChange={v => set({
        videoChecksPerYear: v
      })} max={12} data-id="element-4248" data-mp="3lvmmk8m6t8rz" />
        <SettingNumber label="Eerste levering na (dagen)" hint="Na starten of hervatten." value={sub.leadDays} onChange={v => set({
        leadDays: v
      })} max={60} data-id="element-4249" data-mp="3vtsogq7zuewu" />
        <SettingNumber label="Aanpassen tot (dagen voor levering)" hint="Daarna ligt de levering vast." value={sub.changeUntilDays} onChange={v => set({
        changeUntilDays: v
      })} max={30} data-id="element-4250" data-mp="21hclpzeepiwq" />
        <div data-id="element-4251" data-mp="1k78o4w93gajl">
          <label htmlFor={intervalsId} className="text-[13px] font-medium text-ink" data-id="element-4252" data-mp="1k6yyxavvi7ey">
            Ritmes om uit te kiezen (weken)
          </label>
          <span className="mt-0.5 block text-[12px] leading-snug text-muted" data-id="element-4253" data-mp="1f6ko5l2w0jd8">Gescheiden door een komma.</span>
          <input id={intervalsId} value={intervalsText} onChange={e => setIntervalsText(e.target.value)} onBlur={commitIntervals} inputMode="numeric" aria-invalid={intervalsError ? true : undefined} aria-describedby={intervalsError ? `${intervalsId}-error` : undefined} className={`${inputCls} mt-1.5 sm:max-w-[16rem]`} data-id="element-4254" data-mp="2cu353j6pcrsb" />
          {intervalsError && <span id={`${intervalsId}-error`} role="alert" className="mt-1 block text-[12px] text-clay" data-id="element-4255" data-mp="mj887tr8f9sw">
              {intervalsError}
            </span>}
        </div>
        <Field label="Standaardritme" hint="Wat een nieuw abonnement krijgt." data-id="element-4256" data-mp="2g55uds8d24zh">
          <select value={sub.defaultIntervalWeeks} onChange={e => set({
          defaultIntervalWeeks: Number(e.target.value)
        })} className={`${inputCls} sm:max-w-[11rem]`} data-id="element-4257" data-mp="21gjd3kz6b35m">
            {sub.intervals.map(w => <option key={w} value={w} data-id="element-4258" data-mp="2u5gm0xhhvnlp">
                Elke {w} weken
              </option>)}
          </select>
        </Field>
      </div>

      <fieldset className="mt-6 border-t border-line pt-5" data-id="element-4259" data-mp="2k7aikbg1i9eq">
        <legend className="text-[13px] font-medium text-ink" data-id="element-4260" data-mp="2k8vb5visni9y">Pauzekeuzes</legend>
        <p className="mt-0.5 text-[12px] text-muted" data-id="element-4261" data-mp="2p3r89a2w0vtx">In Mijn omgeving. De middelste wordt voorgesteld bij “ik heb nog genoeg”.</p>
        <ul className="mt-3 space-y-2" data-id="element-4262" data-mp="1dqf6r5ulopax">
          {sub.pauseOptions.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-4263" data-mp="1do2kight7ss2" data-mpi={i}>
              <label className="w-24 shrink-0" data-id="element-4264" data-mp="3rwwrya8t4wd6">
                <span className="sr-only" data-id="element-4265" data-mp="3mz3h33wk7e6k">Pauzekeuze {i + 1}: aantal weken</span>
                <NumberInput value={p.weeks} onChange={w => w !== undefined && setPause(i, {
              weeks: w
            })} min={1} max={52} data-id="element-4266" data-mp="3n1hdbg2z6wfp" />
              </label>
              <label className="min-w-0 flex-1" data-id="element-4267" data-mp="rn2qksn0m4m">
                <span className="sr-only" data-id="element-4268" data-mp="q2bb41mdjaz">Pauzekeuze {i + 1}: tekst voor de klant</span>
                <input value={p.label} onChange={e => setPause(i, {
              label: e.target.value
            })} className={inputCls} placeholder="Bijv. 2 weken" data-id="element-4269" data-mp="397yje7cr4vyv" />
              </label>
              <button type="button" onClick={() => set({
            pauseOptions: sub.pauseOptions.filter((_, j) => j !== i)
          })} disabled={sub.pauseOptions.length <= 1} aria-label={`Pauzekeuze ${i + 1} weghalen`} title={sub.pauseOptions.length <= 1 ? 'Er moet minstens één pauzekeuze blijven' : undefined} className={`${btnIcon} mt-0.5`} data-id="element-4270" data-mp="3i11ysl6yjiw5">
                <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4271" data-mp="off1m4bo5u8f" />
              </button>
            </li>)}
        </ul>
        <button type="button" onClick={() => {
        const last = sub.pauseOptions[sub.pauseOptions.length - 1];
        const weeks = (last?.weeks ?? 2) + 2;
        set({
          pauseOptions: [...sub.pauseOptions, {
            weeks,
            label: `${weeks} weken`
          }]
        });
      }} className={`${btnGhost} mt-3`} data-id="element-4272" data-mp="y8cf1w1t0t06">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4273" data-mp="ajt93n688sb9" />
          Pauzekeuze toevoegen
        </button>
      </fieldset>
    </Panel>;
}